import { getGestureRecognizer, nextVideoTimestamp } from '../mediapipeRuntime'

// Shared finger-drawing state for play mode. When enabled, the index fingertip
// (landmark 8) paints onto the overlay. Strokes are normalized (0-1) so they map
// through the same cover/mirror transform as everything else.
export const drawState = {
    enabled: false,
    color: '#f4813f',
    thickness: 4,
    strokes: [], // [{ color, thickness, points: [{x,y}] }]
    _pen: false, // internal: currently drawing a stroke
}
export function clearDrawing() {
    drawState.strokes = []
    drawState._pen = false
}

const GESTURE_LABELS = {
    Thumb_Up: 'Thumbs up',
    Thumb_Down: 'Thumbs down',
    Open_Palm: 'Open palm',
    Closed_Fist: 'Closed fist',
    Victory: 'Victory',
    Pointing_Up: 'Pointing up',
    ILoveYou: 'I love you',
}

function boxFromLandmarks(lm) {
    let minX = 1
    let minY = 1
    let maxX = 0
    let maxY = 0
    for (const p of lm) {
        if (p.x < minX) minX = p.x
        if (p.y < minY) minY = p.y
        if (p.x > maxX) maxX = p.x
        if (p.y > maxY) maxY = p.y
    }
    // Pad the raw landmark hull a little so the box wraps the whole hand.
    const padX = (maxX - minX) * 0.15
    const padY = (maxY - minY) * 0.15
    return {
        x: Math.max(0, minX - padX),
        y: Math.max(0, minY - padY),
        w: Math.min(1, maxX + padX) - Math.max(0, minX - padX),
        h: Math.min(1, maxY + padY) - Math.max(0, minY - padY),
    }
}

export default function createGestureDetector() {
    let recognizer = null

    return {
        id: 'gesture',
        label: 'Gesture Detection',
        accent: '#14b8a6',

        async load() {
            if (!recognizer) recognizer = await getGestureRecognizer()
        },

        async detect({ video }) {
            if (!recognizer) return null
            const ts = nextVideoTimestamp(performance.now())
            let result
            try {
                result = recognizer.recognizeForVideo(video, ts)
            } catch (e) {
                return null
            }
            const landmarks = result.landmarks || []
            const boxes = landmarks.map((lm, i) => {
                const top = result.gestures?.[i]?.[0]
                const hand = result.handedness?.[i]?.[0]
                const name =
                    top && top.categoryName !== 'None'
                        ? GESTURE_LABELS[top.categoryName] || top.categoryName
                        : 'Hand'
                return {
                    ...boxFromLandmarks(lm),
                    label: hand ? `${name} (${hand.categoryName})` : name,
                    score: top?.score ?? 1,
                    // Raw landmarks so the overlay can draw the hand skeleton.
                    landmarks: lm,
                }
            })

            // Finger drawing: follow the index fingertip (landmark 8) of the
            // first hand while enabled. Closing the hand (Closed Fist) or an
            // Open Palm lifts the pen, so you can move without drawing; a break
            // (no hand) also ends the stroke.
            if (drawState.enabled) {
                const g0 = result.gestures?.[0]?.[0]?.categoryName
                const penUpGesture =
                    g0 === 'Closed_Fist' || g0 === 'Open_Palm'
                const tip = landmarks[0]?.[8]
                if (tip && !penUpGesture) {
                    if (!drawState._pen) {
                        drawState.strokes.push({
                            color: drawState.color,
                            thickness: drawState.thickness,
                            points: [],
                        })
                        drawState._pen = true
                    }
                    drawState.strokes[drawState.strokes.length - 1].points.push({
                        x: tip.x,
                        y: tip.y,
                    })
                } else {
                    drawState._pen = false
                }
            }
            // Render each stroke as a polyline through the overlay painter.
            drawState.strokes.forEach((s) => {
                if (s.points.length > 1)
                    boxes.push({
                        path: s.points,
                        accent: s.color,
                        lineWidth: s.thickness,
                    })
            })

            // Count real hands from landmarks (not `boxes`, which also holds the
            // finger-drawing strokes — that made the count jump, bug 45).
            const handCount = landmarks.length
            // One label per hand, so both hands' gestures show (bug 44).
            const gestureNames = landmarks
                .map((_, i) => {
                    const g = result.gestures?.[i]?.[0]
                    return g && g.categoryName !== 'None'
                        ? GESTURE_LABELS[g.categoryName] || g.categoryName
                        : null
                })
                .filter(Boolean)
            const gestureText = gestureNames.length
                ? gestureNames.join(', ')
                : handCount
                  ? 'None'
                  : '—'

            return {
                boxes,
                insights: [
                    {
                        label: 'Hand Detected',
                        value: handCount ? 'YES' : 'NO',
                    },
                    { label: 'Hands', value: handCount },
                    { label: 'Gesture', value: gestureText },
                ],
            }
        },

        reset() {
            clearDrawing()
        },
        dispose() {
            recognizer = null
        },
    }
}
